import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Range } from "react-range";
import { useSearchPageProductsQuery } from "../slices/productSlice";
import { useGetCategoriesQuery } from "../slices/categorySlice";
import { addToCart } from "../slices/cartSlice";
import Modal from "../components/layout/Modal";
import SearchProductCard from "../components/products/SearchProductCard";
import Pagination from "../components/utilComponents/Pagination";
import CustomDropdownNoSearch from "../components/utilComponents/CustomDropdownNoSearch";
import fullYellowStar from "../assets/images/fullYellowStar.svg";
import fullGrayStar from "../assets/images/fullGrayStar.svg";

const Search = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("query") || "";

  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
  const [isPriceModalVisible, setIsPriceModalVisible] = useState(false);
  const [isRatingModalVisible, setIsRatingModalVisible] = useState(false);
  const [isSortModalVisible, setIsSortModalVisible] = useState(false);

  const PRICE_MIN = 0;
  const PRICE_MAX = 1000000;

  const [loadingDelay, setLoadingDelay] = useState(false);
  const [filters, setFilters] = useState({
    category: [],
    price: [PRICE_MIN, PRICE_MAX],
    rating: 0,
  });

  const [isCategoryExpanded, setIsCategoryExpanded] = useState(false);

  const [location] = useState(localStorage.getItem("location"));
  const [page, setPage] = useState(1);
  const [limit] = useState(16);
  const [sort, setSort] = useState("price");

  const { data: categories, isLoading: isCategoriesLoading } =
    useGetCategoriesQuery();

  const { data, isLoading } = useSearchPageProductsQuery(
    {
      query,
      location,
      filters,
      page,
      limit,
      sort,
    },
    { refetchOnMountOrArgChange: true }, // Enable re-fetching when args change
  );

  useEffect(() => {
    if (isLoading) {
      setLoadingDelay(true);
      const timer = setTimeout(() => setLoadingDelay(false), 500); // Delay for 500ms
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  const renderProducts = () =>
    data?.products.map((product) => (
      <SearchProductCard
        key={product.id}
        image={product.images[0]}
        title={product.title}
        originalPrice={product.originalPrice}
        discountedPrice={product.discountedPrice}
        vendor={product.vendor.storeName}
        vendorId={product.vendor._id}
        productId={product._id}
        rating={product.averageRating}
        reviews={product.numReviews}
        onAddToCart={(item) => dispatch(addToCart(item))}
        onClickProductCard={() => navigate(`/products/${product._id}`)}
      />
    ));

  return (
    <div className="flex flex-col lg:flex-row mx-auto lg:container">
      {/* Left Sidebar for Desktop Filters */}
      <div className="w-1/5  p-4 border-r mr-4 hidden lg:block">
        {/* Category Filter */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold font-nunito">Category</h3>
            <button
              type="button"
              className="text-lg font-bold"
              onClick={() => setIsCategoryExpanded(!isCategoryExpanded)}
            >
              {isCategoryExpanded ? (
                <span className="material-symbols-rounded">remove</span>
              ) : (
                <span className="material-symbols-rounded">add</span>
              )}
            </button>
          </div>
          <div
            className={`overflow-hidden transition-all duration-600`}
            style={{
              height: isCategoryExpanded ? "auto" : "0",
            }}
          >
            <div className="mt-2">
              {isCategoriesLoading
                ? // Skeleton Loader for Categories
                  Array.from({ length: 5 }).map((_, index) => (
                    <div
                      key={index}
                      className="w-full h-4 bg-gray-200 animate-pulse rounded-md mb-2"
                    ></div>
                  ))
                : // Render Category List
                  categories?.map((category) => (
                    <div
                      key={category._id}
                      className={`text-xs text-agLightGray cursor-pointer p-2 rounded-md ${
                        filters.category[0] === category.title ? "bg-agBg" : ""
                      }`}
                      onClick={() => {
                        setFilters({
                          ...filters,
                          category: [category.title], // Replace with the clicked category
                        });
                      }}
                    >
                      {category.title}
                    </div>
                  ))}
            </div>
          </div>
        </div>

        {/* Price Filter */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-12">
            <h3 className="text-sm font-semibold font-nunito">Price (₦)</h3>
            <button
              type="button"
              className="text-xs font-semibold text-agBlue"
              onClick={() =>
                setFilters({ ...filters, price: [PRICE_MIN, PRICE_MAX] })
              } // Reset to default range
            >
              APPLY
            </button>
          </div>
          <div className="mt-4">
            {/* Show loading skeleton for price slider */}
            {isLoading ? (
              <div className="h-10 bg-gray-200 animate-pulse rounded-md mb-2"></div>
            ) : (
              <>
                {/* React-Range Slider */}
                <Range
                  step={1}
                  min={PRICE_MIN}
                  max={PRICE_MAX}
                  values={filters.price}
                  onChange={(values) =>
                    setFilters({ ...filters, price: values })
                  }
                  renderTrack={({ props, children }) => (
                    <div
                      {...props}
                      className="w-full h-2 bg-gray-200 rounded-full"
                      style={{ position: "relative" }}
                    >
                      <div
                        className="absolute bg-agPrimary h-2 rounded-full"
                        style={{
                          left: `${((filters.price[0] - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100}%`,
                          right: `${100 - ((filters.price[1] - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100}%`,
                        }}
                      />
                      {children}
                    </div>
                  )}
                  renderThumb={({ props, index }) => (
                    <div
                      {...props}
                      className="w-4 h-4 bg-agPrimary rounded-full shadow-md flex items-center justify-center cursor-pointer"
                    >
                      {/* Conditionally render only the upper bound price */}
                      {index === 1 && (
                        <span className="absolute text-xs -top-6 right-0">
                          ₦{filters.price[index].toLocaleString()}
                        </span>
                      )}
                    </div>
                  )}
                />
              </>
            )}
          </div>
          <div className="flex justify-between mt-4">
            {/* Min Price Input */}
            {isLoading ? (
              <div className="w-24 h-10 bg-gray-200 animate-pulse rounded-md"></div>
            ) : (
              <input
                type="text"
                value={filters.price[0].toLocaleString()} // Format the number as currency
                className="w-24 text-xs text-agLightGray border rounded-md text-sm p-2 text-center"
                onChange={(e) => {
                  const numericValue = Number(e.target.value.replace(/,/g, "")); // Remove commas before updating the state
                  setFilters({
                    ...filters,
                    price: [
                      Math.min(numericValue || 0, filters.price[1]),
                      filters.price[1],
                    ],
                  });
                }}
              />
            )}
            <span className="text-sm font-bold text-gray-700 mx-2">-</span>
            {/* Max Price Input */}
            {isLoading ? (
              <div className="w-24 h-10 bg-gray-200 animate-pulse rounded-md"></div>
            ) : (
              <input
                type="text"
                value={filters.price[1].toLocaleString()} // Format the number as currency
                className="w-24 text-xs text-agLightGray border rounded-md text-sm p-2 text-center"
                onChange={(e) => {
                  const numericValue = Number(e.target.value.replace(/,/g, "")); // Remove commas before updating the state
                  setFilters({
                    ...filters,
                    price: [
                      filters.price[0],
                      Math.max(numericValue || PRICE_MAX, filters.price[0]),
                    ],
                  });
                }}
              />
            )}
          </div>
        </div>

        {/* Rating Filter */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold font-nunito mb-4">Rating</h3>
          {isLoading ? (
            // Display a skeleton loader when loading
            <div className="space-y-2">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="h-6 bg-gray-200 animate-pulse rounded-md"
                ></div>
              ))}
            </div>
          ) : (
            // Render the rating filter when not loading
            [4, 3, 2, 1].map((rating) => (
              <label
                key={rating}
                className="flex items-center space-x-4 cursor-pointer mb-2"
              >
                {/* Custom Styled Radio Button */}
                <input
                  type="radio"
                  value={rating}
                  name="rating"
                  className="hidden peer" // Hide the default radio button
                  onChange={() => setFilters({ ...filters, rating })}
                />
                <span className="w-4 h-4 rounded-full border-2 border-agLightGray peer-checked:bg-agPrimary"></span>

                {/* Stars */}
                <div className="flex items-center space-x-0">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <img
                      key={star}
                      src={star <= rating ? fullYellowStar : fullGrayStar}
                      alt={`${star} star`}
                      className="w-5 h-5"
                    />
                  ))}
                  <span className="text-xs text-gray-500 font-nunito ml-2">
                    & above
                  </span>
                </div>
              </label>
            ))
          )}
        </div>
      </div>

      {/* Mobile Filters */}
      <div className="lg:hidden flex flex-col w-full space-y-4 px-2">
        {/* Mobile Filter Buttons */}
        <div className="flex items-center space-x-4 overflow-x-auto my-2">
          <button
            className={`flex-1 flex items-center justify-between rounded-md px-4 py-2 text-sm font-nunito ${
              filters.category.length > 0
                ? "bg-agPrimaryLight text-agBlack"
                : "bg-gray-100 text-agBlack"
            }`}
            onClick={() => setIsCategoryModalVisible(true)}
          >
            Category
            <span className="material-symbols-rounded text-sm">
              keyboard_arrow_down
            </span>
          </button>
          <button
            className={`flex-1 flex items-center justify-between rounded-md px-4 py-2 text-sm font-nunito ${
              filters.price[0] !== PRICE_MIN || filters.price[1] !== PRICE_MAX
                ? "bg-agPrimaryLight text-agBlack"
                : "bg-gray-100 text-agBlack"
            }`}
            onClick={() => setIsPriceModalVisible(true)}
          >
            Price
            <span className="material-symbols-rounded text-sm">
              keyboard_arrow_down
            </span>
          </button>
          <button
            className={`flex-1 flex items-center justify-between rounded-md px-4 py-2 text-sm font-nunito ${
              filters.rating > 0
                ? "bg-agPrimaryLight text-agBlack"
                : "bg-gray-100 text-agBlack"
            }`}
            onClick={() => setIsRatingModalVisible(true)}
          >
            Rating
            <span className="material-symbols-rounded text-sm">
              keyboard_arrow_down
            </span>
          </button>
          <button
            className={`flex-1 flex items-center justify-between rounded-md px-4 py-2 text-sm font-nunito ${
              sort !== "price"
                ? "bg-agPrimaryLight text-agBlack"
                : "bg-gray-100 text-agBlack"
            }`}
            onClick={() => setIsSortModalVisible(true)}
          >
            Sort
            <span className="material-symbols-rounded text-sm">
              keyboard_arrow_down
            </span>
          </button>
        </div>
      </div>

      {/* Modals for Mobile Filters */}
      {/* Category Modal */}
      <Modal
        isVisible={isCategoryModalVisible}
        onClose={() => setIsCategoryModalVisible(false)}
      >
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-4">Category</h3>
          {isCategoriesLoading
            ? Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className="w-full h-4 bg-gray-200 animate-pulse rounded-md mb-2"
                ></div>
              ))
            : categories?.map((category) => (
                <div
                  key={category._id}
                  className={`text-sm text-agLightGray cursor-pointer p-2 rounded-md ${
                    filters.category[0] === category.title ? "bg-agBg" : ""
                  }`}
                  onClick={() => {
                    setFilters({ ...filters, category: [category.title] });
                    setIsCategoryModalVisible(false);
                  }}
                >
                  {category.title}
                </div>
              ))}
        </div>
      </Modal>

      {/* Price Modal */}
      <Modal
        isVisible={isPriceModalVisible}
        onClose={() => setIsPriceModalVisible(false)}
      >
        <div className="px-4 py-8">
          <h3 className="text-lg font-semibold mb-4">Price</h3>
          <Range
            step={1}
            min={PRICE_MIN}
            max={PRICE_MAX}
            values={filters.price}
            onChange={(values) => setFilters({ ...filters, price: values })}
            renderTrack={({ props, children }) => (
              <div
                {...props}
                className="w-full h-2 bg-gray-200 rounded-full"
                style={{ position: "relative" }}
              >
                <div
                  className="absolute bg-agPrimary h-2 rounded-full"
                  style={{
                    left: `${((filters.price[0] - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100}%`,
                    right: `${100 - ((filters.price[1] - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100}%`,
                  }}
                />
                {children}
              </div>
            )}
            renderThumb={({ props, index }) => (
              <div
                {...props}
                className="w-4 h-4 bg-agPrimary rounded-full shadow-md flex items-center justify-center cursor-pointer"
              >
                {index === 1 && (
                  <span className="absolute text-xs -top-6 right-0">
                    ₦{filters.price[index].toLocaleString()}
                  </span>
                )}
              </div>
            )}
          />
        </div>
      </Modal>

      {/* Rating Modal */}
      <Modal
        isVisible={isRatingModalVisible}
        onClose={() => setIsRatingModalVisible(false)}
      >
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-4">Rating</h3>
          {[4, 3, 2, 1].map((rating) => (
            <label
              key={rating}
              className="flex items-center space-x-4 cursor-pointer mb-2"
            >
              <input
                type="radio"
                value={rating}
                name="rating"
                className="hidden peer"
                onChange={() => setFilters({ ...filters, rating })}
              />
              <span className="w-4 h-4 rounded-full border-2 border-agLightGray peer-checked:bg-agPrimary"></span>
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <img
                    key={star}
                    src={star <= rating ? fullYellowStar : fullGrayStar}
                    alt={`${star} star`}
                    className="w-5 h-5"
                  />
                ))}
                <span className="ml-2 text-sm text-gray-500">& above</span>
              </div>
            </label>
          ))}
        </div>
      </Modal>

      {/* Sort Modal */}
      <Modal
        isVisible={isSortModalVisible}
        onClose={() => setIsSortModalVisible(false)}
      >
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-4">Sort</h3>
          <div className="flex flex-col space-y-2">
            {[
              { value: "price", label: "Price: Low to High" },
              { value: "-price", label: "Price: High to Low" },
              { value: "rating", label: "Rating: High to Low" },
              { value: "-rating", label: "Rating: Low to High" },
            ].map((option) => (
              <button
                key={option.value}
                className={`text-sm p-2 rounded-md text-left ${
                  sort === option.value ? "bg-agBg" : "bg-white"
                }`}
                onClick={() => {
                  setSort(option.value);
                  setIsSortModalVisible(false);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </Modal>

      {/* Right Main Content */}
      <div className="w-full lg:w-3/4 p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-nunito font-regular text-sm md:text-md md:font-semibold  text-agBlack text-md">
            Search Results for "{query}"
          </h3>

          {/* Sorting Dropdown */}
          {/* <div className="hidden lg:block w-56 relative">
            <CustomDropdownNoSearch
              options={[
                { value: "price", label: "Price: Low to High" },
                { value: "-price", label: "Price: High to Low" },
                { value: "rating", label: "Rating: High to Low" },
                { value: "-rating", label: "Rating: Low to High" },
              ].map((option) => option.label)} // Pass only labels for display
              value={
                {
                  price: "Price (Lowest to Highest)",
                  "-price": "Price (Highest to Lowest)",
                  rating: "Rating (Highest to Lowest)",
                  "-rating": "Rating (Lowest to Highest)",
                }[sort]
              }
              onChange={(label) =>
                setSort(
                  {
                    "Price (Lowest to Highest)": "price",
                    "Price (Highest to Lowest)": "-price",
                    "Rating (Highest to Lowest)": "rating",
                    "Rating (Lowest to Highest)": "-rating",
                  }[label],
                )
              }
              placeholder="Sort by"
            />
            {isLoading && (
              <div className="absolute top-1/2 right-0 transform -translate-y-1/2">
                <span className="loader" />
              </div>
            )}
          </div> */}
          <div className="hidden lg:block w-56 relative">
            <CustomDropdownNoSearch
              options={[
                "Price: Low to High",
                "Price: High to Low",
                "Rating: High to Low",
                "Rating: Low to High",
              ]} // Pass only labels as strings
              value={
                {
                  price: "Price: Low to High",
                  "-price": "Price: High to Low",
                  rating: "Rating: High to Low",
                  "-rating": "Rating: Low to High",
                }[sort] || "Sort by"
              } // Map value to label
              onChange={(selectedLabel) => {
                const sortMapping = {
                  "Price: Low to High": "price",
                  "Price: High to Low": "-price",
                  "Rating: High to Low": "rating",
                  "Rating: Low to High": "-rating",
                };
                setSort(sortMapping[selectedLabel]); // Map label back to value
              }}
              placeholder="Sort by"
            />
            {isLoading && (
              <div className="absolute top-1/2 right-0 transform -translate-y-1/2">
                <span className="loader" />
              </div>
            )}
          </div>
        </div>

        {/* Product Grid */}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {loadingDelay ? (
            // Render skeleton loaders for the grid
            Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="h-64 bg-gray-200 animate-pulse rounded-md"
              ></div>
            ))
          ) : isLoading ? (
            // <p className="text-center text-sm font-nunito text-gray-500">
            //   Loading... 🚨
            // </p>
            Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="h-64 bg-gray-200 animate-pulse rounded-md"
              ></div>
            ))
          ) : data?.products.length > 0 ? (
            renderProducts()
          ) : (
            <p className="text-center text-sm font-nunito text-gray-500">
              No products found for your search.
            </p>
          )}
        </div>

        {/* Pagination */}
        {data?.totalProducts > limit && (
          <Pagination
            currentPage={page}
            totalPages={data?.totalPages || 1}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
};

export default Search;

// // WORKING SEARCH
// // import React, { useState, useEffect } from "react";
// // import { useDispatch } from "react-redux";
// // import { useSearchParams, useNavigate } from "react-router-dom";
// // import { Range } from "react-range";
// // import { useSearchPageProductsQuery } from "../slices/productSlice";
// // import { useGetCategoriesQuery } from "../slices/categorySlice";
// // import { addToCart } from "../slices/cartSlice";
// // import Modal from "../components/layout/Modal";
// // import SearchProductCard from "../components/products/SearchProductCard";
// // import Pagination from "../components/utilComponents/Pagination";
// // import CustomDropdownNoSearch from "../components/utilComponents/CustomDropdownNoSearch";
// // import fullYellowStar from "../assets/images/fullYellowStar.svg";
// // import fullGrayStar from "../assets/images/fullGrayStar.svg";

// // const Search = () => {
// //   const navigate = useNavigate();
// //   const dispatch = useDispatch();
// //   const [searchParams] = useSearchParams();
// //   const query = searchParams.get("query") || "";

// //   const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
// //   const [isPriceModalVisible, setIsPriceModalVisible] = useState(false);
// //   const [isRatingModalVisible, setIsRatingModalVisible] = useState(false);
// //   const [isSortModalVisible, setIsSortModalVisible] = useState(false);

// //   const PRICE_MIN = 0;
// //   const PRICE_MAX = 1000000;

// //   const [loadingDelay, setLoadingDelay] = useState(false);
// //   const [filters, setFilters] = useState({
// //     category: [],
// //     price: [PRICE_MIN, PRICE_MAX],
// //     rating: 0,
// //   });

// //   const [isCategoryExpanded, setIsCategoryExpanded] = useState(false);

// //   const [location] = useState(localStorage.getItem("location"));
// //   const [page, setPage] = useState(1);
// //   const [limit] = useState(16);
// //   const [sort, setSort] = useState("price");

// //   const { data: categories, isLoading: isCategoriesLoading } =
// //     useGetCategoriesQuery();

// //   const { data, isLoading } = useSearchPageProductsQuery(
// //     {
// //       query,
// //       location,
// //       filters,
// //       page,
// //       limit,
// //       sort,
// //     },
// //     { refetchOnMountOrArgChange: true }, // Enable re-fetching when args change
// //   );

// //   useEffect(() => {
// //     if (isLoading) {
// //       setLoadingDelay(true);
// //       const timer = setTimeout(() => setLoadingDelay(false), 500); // Delay for 500ms
// //       return () => clearTimeout(timer);
// //     }
// //   }, [isLoading]);

// //   const renderProducts = () =>
// //     data?.products.map((product) => (
// //       <SearchProductCard
// //         key={product.id}
// //         image={product.images[0]}
// //         title={product.title}
// //         originalPrice={product.originalPrice}
// //         discountedPrice={product.discountedPrice}
// //         vendor={product.vendor.storeName}
// //         vendorId={product.vendor._id}
// //         productId={product._id}
// //         rating={product.averageRating}
// //         reviews={product.numReviews}
// //         onAddToCart={(item) => dispatch(addToCart(item))}
// //         onClickProductCard={() => navigate(`/products/${product._id}`)}
// //       />
// //     ));

// //   return (
// //     <div className="flex flex-col lg:flex-row mx-auto lg:container">
// //       {/* Left Sidebar for Desktop Filters */}
// //       <div className="w-1/5  p-4 border-r mr-4 hidden lg:block">
// //         {/* Category Filter */}
// //         <div className="mb-6">
// //           <div className="flex items-center justify-between">
// //             <h3 className="text-sm font-semibold font-nunito">Category</h3>
// //             <button
// //               type="button"
// //               className="text-lg font-bold"
// //               onClick={() => setIsCategoryExpanded(!isCategoryExpanded)}
// //             >
// //               {isCategoryExpanded ? (
// //                 <span className="material-symbols-rounded">remove</span>
// //               ) : (
// //                 <span className="material-symbols-rounded">add</span>
// //               )}
// //             </button>
// //           </div>
// //           <div
// //             className={`overflow-hidden transition-all duration-600`}
// //             style={{
// //               height: isCategoryExpanded ? "auto" : "0",
// //             }}
// //           >
// //             <div className="mt-2">
// //               {isCategoriesLoading
// //                 ? // Skeleton Loader for Categories
// //                   Array.from({ length: 5 }).map((_, index) => (
// //                     <div
// //                       key={index}
// //                       className="w-full h-4 bg-gray-200 animate-pulse rounded-md mb-2"
// //                     ></div>
// //                   ))
// //                 : // Render Category List
// //                   categories?.map((category) => (
// //                     <div
// //                       key={category._id}
// //                       className={`text-xs text-agLightGray cursor-pointer p-2 rounded-md ${
// //                         filters.category[0] === category.title ? "bg-agBg" : ""
// //                       }`}
// //                       onClick={() => {
// //                         setFilters({
// //                           ...filters,
// //                           category: [category.title], // Replace with the clicked category
// //                         });
// //                       }}
// //                     >
// //                       {category.title}
// //                     </div>
// //                   ))}
// //             </div>
// //           </div>
// //         </div>

// //         {/* Price Filter */}
// //         <div className="mb-6">
// //           <div className="flex items-center justify-between mb-12">
// //             <h3 className="text-sm font-semibold font-nunito">Price (₦)</h3>
// //             <button
// //               type="button"
// //               className="text-xs font-semibold text-agBlue"
// //               onClick={() =>
// //                 setFilters({ ...filters, price: [PRICE_MIN, PRICE_MAX] })
// //               } // Reset to default range
// //             >
// //               APPLY
// //             </button>
// //           </div>
// //           <div className="mt-4">
// //             {/* Show loading skeleton for price slider */}
// //             {isLoading ? (
// //               <div className="h-10 bg-gray-200 animate-pulse rounded-md mb-2"></div>
// //             ) : (
// //               <>
// //                 {/* React-Range Slider */}
// //                 <Range
// //                   step={1}
// //                   min={PRICE_MIN}
// //                   max={PRICE_MAX}
// //                   values={filters.price}
// //                   onChange={(values) =>
// //                     setFilters({ ...filters, price: values })
// //                   }
// //                   renderTrack={({ props, children }) => (
// //                     <div
// //                       {...props}
// //                       className="w-full h-2 bg-gray-200 rounded-full"
// //                       style={{ position: "relative" }}
// //                     >
// //                       <div
// //                         className="absolute bg-agPrimary h-2 rounded-full"
// //                         style={{
// //                           left: `${((filters.price[0] - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100}%`,
// //                           right: `${100 - ((filters.price[1] - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100}%`,
// //                         }}
// //                       />
// //                       {children}
// //                     </div>
// //                   )}
// //                   renderThumb={({ props, index }) => (
// //                     <div
// //                       {...props}
// //                       className="w-4 h-4 bg-agPrimary rounded-full shadow-md flex items-center justify-center cursor-pointer"
// //                     >
// //                       {/* Conditionally render only the upper bound price */}
// //                       {index === 1 && (
// //                         <span className="absolute text-xs -top-6 right-0">
// //                           ₦{filters.price[index].toLocaleString()}
// //                         </span>
// //                       )}
// //                     </div>
// //                   )}
// //                 />
// //               </>
// //             )}
// //           </div>
// //           <div className="flex justify-between mt-4">
// //             {/* Min Price Input */}
// //             {isLoading ? (
// //               <div className="w-24 h-10 bg-gray-200 animate-pulse rounded-md"></div>
// //             ) : (
// //               <input
// //                 type="text"
// //                 value={filters.price[0].toLocaleString()} // Format the number as currency
// //                 className="w-24 text-xs text-agLightGray border rounded-md text-sm p-2 text-center"
// //                 onChange={(e) => {
// //                   const numericValue = Number(e.target.value.replace(/,/g, "")); // Remove commas before updating the state
// //                   setFilters({
// //                     ...filters,
// //                     price: [
// //                       Math.min(numericValue || 0, filters.price[1]),
// //                       filters.price[1],
// //                     ],
// //                   });
// //                 }}
// //               />
// //             )}
// //             <span className="text-sm font-bold text-gray-700 mx-2">-</span>
// //             {/* Max Price Input */}
// //             {isLoading ? (
// //               <div className="w-24 h-10 bg-gray-200 animate-pulse rounded-md"></div>
// //             ) : (
// //               <input
// //                 type="text"
// //                 value={filters.price[1].toLocaleString()} // Format the number as currency
// //                 className="w-24 text-xs text-agLightGray border rounded-md text-sm p-2 text-center"
// //                 onChange={(e) => {
// //                   const numericValue = Number(e.target.value.replace(/,/g, "")); // Remove commas before updating the state
// //                   setFilters({
// //                     ...filters,
// //                     price: [
// //                       filters.price[0],
// //                       Math.max(numericValue || PRICE_MAX, filters.price[0]),
// //                     ],
// //                   });
// //                 }}
// //               />
// //             )}
// //           </div>
// //         </div>

// //         {/* Rating Filter */}
// //         <div className="mb-6">
// //           <h3 className="text-sm font-semibold font-nunito mb-4">Rating</h3>
// //           {isLoading ? (
// //             // Display a skeleton loader when loading
// //             <div className="space-y-2">
// //               {[...Array(4)].map((_, index) => (
// //                 <div
// //                   key={index}
// //                   className="h-6 bg-gray-200 animate-pulse rounded-md"
// //                 ></div>
// //               ))}
// //             </div>
// //           ) : (
// //             // Render the rating filter when not loading
// //             [4, 3, 2, 1].map((rating) => (
// //               <label
// //                 key={rating}
// //                 className="flex items-center space-x-4 cursor-pointer mb-2"
// //               >
// //                 {/* Custom Styled Radio Button */}
// //                 <input
// //                   type="radio"
// //                   value={rating}
// //                   name="rating"
// //                   className="hidden peer" // Hide the default radio button
// //                   onChange={() => setFilters({ ...filters, rating })}
// //                 />
// //                 <span className="w-4 h-4 rounded-full border-2 border-agLightGray peer-checked:bg-agPrimary"></span>

// //                 {/* Stars */}
// //                 <div className="flex items-center space-x-0">
// //                   {[1, 2, 3, 4, 5].map((star) => (
// //                     <img
// //                       key={star}
// //                       src={star <= rating ? fullYellowStar : fullGrayStar}
// //                       alt={`${star} star`}
// //                       className="w-5 h-5"
// //                     />
// //                   ))}
// //                   <span className="text-xs text-gray-500 font-nunito ml-2">
// //                     & above
// //                   </span>
// //                 </div>
// //               </label>
// //             ))
// //           )}
// //         </div>
// //       </div>

// //       {/* Mobile Filters */}
// //       <div className="lg:hidden flex flex-col w-full space-y-4 px-2">
// //         {/* Mobile Filter Buttons */}
// //         <div className="flex items-center space-x-4 overflow-x-auto my-2">
// //           <button
// //             className={`flex-1 flex items-center justify-between rounded-md px-4 py-2 text-sm font-nunito ${
// //               filters.category.length > 0
// //                 ? "bg-agPrimaryLight text-agBlack"
// //                 : "bg-gray-100 text-agBlack"
// //             }`}
// //             onClick={() => setIsCategoryModalVisible(true)}
// //           >
// //             Category
// //             <span className="material-symbols-rounded text-sm">
// //               keyboard_arrow_down
// //             </span>
// //           </button>
// //           <button
// //             className={`flex-1 flex items-center justify-between rounded-md px-4 py-2 text-sm font-nunito ${
// //               filters.price[0] !== PRICE_MIN || filters.price[1] !== PRICE_MAX
// //                 ? "bg-agPrimaryLight text-agBlack"
// //                 : "bg-gray-100 text-agBlack"
// //             }`}
// //             onClick={() => setIsPriceModalVisible(true)}
// //           >
// //             Price
// //             <span className="material-symbols-rounded text-sm">
// //               keyboard_arrow_down
// //             </span>
// //           </button>
// //           <button
// //             className={`flex-1 flex items-center justify-between rounded-md px-4 py-2 text-sm font-nunito ${
// //               filters.rating > 0
// //                 ? "bg-agPrimaryLight text-agBlack"
// //                 : "bg-gray-100 text-agBlack"
// //             }`}
// //             onClick={() => setIsRatingModalVisible(true)}
// //           >
// //             Rating
// //             <span className="material-symbols-rounded text-sm">
// //               keyboard_arrow_down
// //             </span>
// //           </button>
// //           <button
// //             className={`flex-1 flex items-center justify-between rounded-md px-4 py-2 text-sm font-nunito ${
// //               sort !== "price"
// //                 ? "bg-agPrimaryLight text-agBlack"
// //                 : "bg-gray-100 text-agBlack"
// //             }`}
// //             onClick={() => setIsSortModalVisible(true)}
// //           >
// //             Sort
// //             <span className="material-symbols-rounded text-sm">
// //               keyboard_arrow_down
// //             </span>
// //           </button>
// //         </div>
// //       </div>

// //       {/* Modals for Mobile Filters */}
// //       {/* Category Modal */}
// //       <Modal
// //         isVisible={isCategoryModalVisible}
// //         onClose={() => setIsCategoryModalVisible(false)}
// //       >
// //         <div className="p-4">
// //           <h3 className="text-lg font-semibold mb-4">Category</h3>
// //           {isCategoriesLoading
// //             ? Array.from({ length: 5 }).map((_, index) => (
// //                 <div
// //                   key={index}
// //                   className="w-full h-4 bg-gray-200 animate-pulse rounded-md mb-2"
// //                 ></div>
// //               ))
// //             : categories?.map((category) => (
// //                 <div
// //                   key={category._id}
// //                   className={`text-sm text-agLightGray cursor-pointer p-2 rounded-md ${
// //                     filters.category[0] === category.title ? "bg-agBg" : ""
// //                   }`}
// //                   onClick={() => {
// //                     setFilters({ ...filters, category: [category.title] });
// //                     setIsCategoryModalVisible(false);
// //                   }}
// //                 >
// //                   {category.title}
// //                 </div>
// //               ))}
// //         </div>
// //       </Modal>

// //       {/* Price Modal */}
// //       <Modal
// //         isVisible={isPriceModalVisible}
// //         onClose={() => setIsPriceModalVisible(false)}
// //       >
// //         <div className="px-4 py-8">
// //           <h3 className="text-lg font-semibold mb-4">Price</h3>
// //           <Range
// //             step={1}
// //             min={PRICE_MIN}
// //             max={PRICE_MAX}
// //             values={filters.price}
// //             onChange={(values) => setFilters({ ...filters, price: values })}
// //             renderTrack={({ props, children }) => (
// //               <div
// //                 {...props}
// //                 className="w-full h-2 bg-gray-200 rounded-full"
// //                 style={{ position: "relative" }}
// //               >
// //                 <div
// //                   className="absolute bg-agPrimary h-2 rounded-full"
// //                   style={{
// //                     left: `${((filters.price[0] - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100}%`,
// //                     right: `${100 - ((filters.price[1] - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100}%`,
// //                   }}
// //                 />
// //                 {children}
// //               </div>
// //             )}
// //             renderThumb={({ props, index }) => (
// //               <div
// //                 {...props}
// //                 className="w-4 h-4 bg-agPrimary rounded-full shadow-md flex items-center justify-center cursor-pointer"
// //               >
// //                 {index === 1 && (
// //                   <span className="absolute text-xs -top-6 right-0">
// //                     ₦{filters.price[index].toLocaleString()}
// //                   </span>
// //                 )}
// //               </div>
// //             )}
// //           />
// //         </div>
// //       </Modal>

// //       {/* Rating Modal */}
// //       <Modal
// //         isVisible={isRatingModalVisible}
// //         onClose={() => setIsRatingModalVisible(false)}
// //       >
// //         <div className="p-4">
// //           <h3 className="text-lg font-semibold mb-4">Rating</h3>
// //           {[4, 3, 2, 1].map((rating) => (
// //             <label
// //               key={rating}
// //               className="flex items-center space-x-4 cursor-pointer mb-2"
// //             >
// //               <input
// //                 type="radio"
// //                 value={rating}
// //                 name="rating"
// //                 className="hidden peer"
// //                 onChange={() => setFilters({ ...filters, rating })}
// //               />
// //               <span className="w-4 h-4 rounded-full border-2 border-agLightGray peer-checked:bg-agPrimary"></span>
// //               <div className="flex items-center">
// //                 {[1, 2, 3, 4, 5].map((star) => (
// //                   <img
// //                     key={star}
// //                     src={star <= rating ? fullYellowStar : fullGrayStar}
// //                     alt={`${star} star`}
// //                     className="w-5 h-5"
// //                   />
// //                 ))}
// //                 <span className="ml-2 text-sm text-gray-500">& above</span>
// //               </div>
// //             </label>
// //           ))}
// //         </div>
// //       </Modal>

// //       {/* Sort Modal */}
// //       <Modal
// //         isVisible={isSortModalVisible}
// //         onClose={() => setIsSortModalVisible(false)}
// //       >
// //         <div className="p-4">
// //           <h3 className="text-lg font-semibold mb-4">Sort</h3>
// //           <div className="flex flex-col space-y-2">
// //             {[
// //               { value: "price", label: "Price: Low to High" },
// //               { value: "-price", label: "Price: High to Low" },
// //               { value: "rating", label: "Rating: High to Low" },
// //               { value: "-rating", label: "Rating: Low to High" },
// //             ].map((option) => (
// //               <button
// //                 key={option.value}
// //                 className={`text-sm p-2 rounded-md text-left ${
// //                   sort === option.value ? "bg-agBg" : "bg-white"
// //                 }`}
// //                 onClick={() => {
// //                   setSort(option.value);
// //                   setIsSortModalVisible(false);
// //                 }}
// //               >
// //                 {option.label}
// //               </button>
// //             ))}
// //           </div>
// //         </div>
// //       </Modal>

// //       {/* Right Main Content */}
// //       <div className="w-full lg:w-3/4 p-4">
// //         <div className="flex justify-between items-center mb-4">
// //           <h3 className="font-nunito font-regular text-sm md:text-md md:font-semibold  text-agBlack text-md">
// //             Search Results for "{query}"
// //           </h3>

// //           {/* Sorting Dropdown */}
// //           <div className="hidden lg:block w-56 relative">
// //             <CustomDropdownNoSearch
// //               options={[
// //                 { value: "price", label: "Price: Low to High" },
// //                 { value: "-price", label: "Price: High to Low" },
// //                 { value: "rating", label: "Rating: High to Low" },
// //                 { value: "-rating", label: "Rating: Low to High" },
// //               ].map((option) => option.label)} // Pass only labels for display
// //               value={
// //                 {
// //                   price: "Price (Lowest to Highest)",
// //                   "-price": "Price (Highest to Lowest)",
// //                   rating: "Rating (Highest to Lowest)",
// //                   "-rating": "Rating (Lowest to Highest)",
// //                 }[sort]
// //               }
// //               onChange={(label) =>
// //                 setSort(
// //                   {
// //                     "Price (Lowest to Highest)": "price",
// //                     "Price (Highest to Lowest)": "-price",
// //                     "Rating (Highest to Lowest)": "rating",
// //                     "Rating (Lowest to Highest)": "-rating",
// //                   }[label],
// //                 )
// //               }
// //               placeholder="Sort by"
// //             />
// //             {isLoading && (
// //               <div className="absolute top-1/2 right-0 transform -translate-y-1/2">
// //                 <span className="loader" />
// //               </div>
// //             )}
// //           </div>
// //         </div>

// //         {/* Product Grid */}

// //         <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
// //           {loadingDelay ? (
// //             // Render skeleton loaders for the grid
// //             Array.from({ length: 8 }).map((_, index) => (
// //               <div
// //                 key={index}
// //                 className="h-64 bg-gray-200 animate-pulse rounded-md"
// //               ></div>
// //             ))
// //           ) : isLoading ? (
// //             <p className="text-center text-sm font-nunito text-gray-500">
// //               Loading... 🚨
// //             </p>
// //           ) : data?.products.length > 0 ? (
// //             renderProducts()
// //           ) : (
// //             <p className="text-center text-sm font-nunito text-gray-500">
// //               No products found for your search.
// //             </p>
// //           )}
// //         </div>

// //         {/* Pagination */}
// //         {data?.totalProducts > limit && (
// //           <Pagination
// //             currentPage={page}
// //             totalPages={data?.totalPages || 1}
// //             onPageChange={setPage}
// //           />
// //         )}
// //       </div>
// //     </div>
// //   );
// // };

// // export default Search;

// // // ===========================================================================================================

// // // import React, { useState, useEffect } from "react";
// // // import { useDispatch } from "react-redux";
// // // import { useSearchParams, useNavigate } from "react-router-dom";
// // // import { Range } from "react-range";
// // // import { useSearchPageProductsQuery } from "../slices/productSlice";
// // // import { useGetCategoriesQuery } from "../slices/categorySlice";
// // // import { addToCart } from "../slices/cartSlice";
// // // import SearchProductCard from "../components/products/SearchProductCard";
// // // import Pagination from "../components/utilComponents/Pagination";
// // // import CustomDropdownNoSearch from "../components/utilComponents/CustomDropdownNoSearch";
// // // import fullYellowStar from "../assets/images/fullYellowStar.svg";
// // // import fullGrayStar from "../assets/images/fullGrayStar.svg";

// // // const Search = () => {
// // //   const navigate = useNavigate();
// // //   const dispatch = useDispatch();
// // //   const [searchParams] = useSearchParams();
// // //   const query = searchParams.get("query") || "";

// // //   const PRICE_MIN = 0;
// // //   const PRICE_MAX = 1000000;

// // //   const [loadingDelay, setLoadingDelay] = useState(false);
// // //   const [filters, setFilters] = useState({
// // //     category: [],
// // //     price: [PRICE_MIN, PRICE_MAX],
// // //     rating: 0,
// // //   });

// // //   const [isCategoryExpanded, setIsCategoryExpanded] = useState(false);

// // //   const [location] = useState(localStorage.getItem("location"));
// // //   const [page, setPage] = useState(1);
// // //   const [limit] = useState(16);
// // //   const [sort, setSort] = useState("price");

// // //   const { data: categories, isLoading: isCategoriesLoading } =
// // //     useGetCategoriesQuery();

// // //   const { data, isLoading } = useSearchPageProductsQuery(
// // //     {
// // //       query,
// // //       location,
// // //       filters,
// // //       page,
// // //       limit,
// // //       sort,
// // //     },
// // //     { refetchOnMountOrArgChange: true }, // Enable re-fetching when args change
// // //   );

// // //   useEffect(() => {
// // //     if (isLoading) {
// // //       setLoadingDelay(true);
// // //       const timer = setTimeout(() => setLoadingDelay(false), 500); // Delay for 500ms
// // //       return () => clearTimeout(timer);
// // //     }
// // //   }, [isLoading]);

// // //   const renderProducts = () =>
// // //     data?.products.map((product) => (
// // //       <SearchProductCard
// // //         key={product.id}
// // //         image={product.images[0]}
// // //         title={product.title}
// // //         originalPrice={product.originalPrice}
// // //         discountedPrice={product.discountedPrice}
// // //         vendor={product.vendor.storeName}
// // //         vendorId={product.vendor._id}
// // //         productId={product._id}
// // //         rating={product.averageRating}
// // //         reviews={product.numReviews}
// // //         onAddToCart={(item) => dispatch(addToCart(item))}
// // //         onClickProductCard={() => navigate(`/products/${product._id}`)}
// // //       />
// // //     ));

// // //   return (
// // //     <div className="flex container">
// // //       {/* Left Sidebar for Filters */}
// // //       <div className="w-1/5  p-4 border-r">
// // //         {/* Category Filter */}
// // //         <div className="mb-6">
// // //           <div className="flex items-center justify-between">
// // //             <h3 className="text-sm font-semibold font-nunito">Category</h3>
// // //             <button
// // //               type="button"
// // //               className="text-lg font-bold"
// // //               onClick={() => setIsCategoryExpanded(!isCategoryExpanded)}
// // //             >
// // //               {isCategoryExpanded ? (
// // //                 <span className="material-symbols-rounded">remove</span>
// // //               ) : (
// // //                 <span className="material-symbols-rounded">add</span>
// // //               )}
// // //             </button>
// // //           </div>
// // //           <div
// // //             className={`overflow-hidden transition-all duration-600`}
// // //             style={{
// // //               height: isCategoryExpanded ? "auto" : "0",
// // //             }}
// // //           >
// // //             <div className="mt-2">
// // //               {isCategoriesLoading
// // //                 ? // Skeleton Loader for Categories
// // //                   Array.from({ length: 5 }).map((_, index) => (
// // //                     <div
// // //                       key={index}
// // //                       className="w-full h-4 bg-gray-200 animate-pulse rounded-md mb-2"
// // //                     ></div>
// // //                   ))
// // //                 : // Render Category List
// // //                   categories?.map((category) => (
// // //                     <div
// // //                       key={category._id}
// // //                       className={`text-xs text-agLightGray cursor-pointer p-2 rounded-md ${
// // //                         filters.category[0] === category.title ? "bg-agBg" : ""
// // //                       }`}
// // //                       onClick={() => {
// // //                         setFilters({
// // //                           ...filters,
// // //                           category: [category.title], // Replace with the clicked category
// // //                         });
// // //                       }}
// // //                     >
// // //                       {category.title}
// // //                     </div>
// // //                   ))}
// // //             </div>
// // //           </div>
// // //         </div>

// // //         {/* Price Filter */}
// // //         <div className="mb-6">
// // //           <div className="flex items-center justify-between mb-12">
// // //             <h3 className="text-sm font-semibold font-nunito">Price (₦)</h3>
// // //             <button
// // //               type="button"
// // //               className="text-xs font-semibold text-agBlue"
// // //               onClick={() =>
// // //                 setFilters({ ...filters, price: [PRICE_MIN, PRICE_MAX] })
// // //               } // Reset to default range
// // //             >
// // //               APPLY
// // //             </button>
// // //           </div>
// // //           <div className="mt-4">
// // //             {/* Show loading skeleton for price slider */}
// // //             {isLoading ? (
// // //               <div className="h-10 bg-gray-200 animate-pulse rounded-md mb-2"></div>
// // //             ) : (
// // //               <>
// // //                 {/* React-Range Slider */}
// // //                 <Range
// // //                   step={1}
// // //                   min={PRICE_MIN}
// // //                   max={PRICE_MAX}
// // //                   values={filters.price}
// // //                   onChange={(values) =>
// // //                     setFilters({ ...filters, price: values })
// // //                   }
// // //                   renderTrack={({ props, children }) => (
// // //                     <div
// // //                       {...props}
// // //                       className="w-full h-2 bg-gray-200 rounded-full"
// // //                       style={{ position: "relative" }}
// // //                     >
// // //                       <div
// // //                         className="absolute bg-agPrimary h-2 rounded-full"
// // //                         style={{
// // //                           left: `${((filters.price[0] - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100}%`,
// // //                           right: `${100 - ((filters.price[1] - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100}%`,
// // //                         }}
// // //                       />
// // //                       {children}
// // //                     </div>
// // //                   )}
// // //                   renderThumb={({ props, index }) => (
// // //                     <div
// // //                       {...props}
// // //                       className="w-4 h-4 bg-agPrimary rounded-full shadow-md flex items-center justify-center cursor-pointer"
// // //                     >
// // //                       {/* Conditionally render only the upper bound price */}
// // //                       {index === 1 && (
// // //                         <span className="absolute text-xs -top-6 right-0">
// // //                           ₦{filters.price[index].toLocaleString()}
// // //                         </span>
// // //                       )}
// // //                     </div>
// // //                   )}
// // //                 />
// // //               </>
// // //             )}
// // //           </div>
// // //           <div className="flex justify-between mt-4">
// // //             {/* Min Price Input */}
// // //             {isLoading ? (
// // //               <div className="w-24 h-10 bg-gray-200 animate-pulse rounded-md"></div>
// // //             ) : (
// // //               <input
// // //                 type="text"
// // //                 value={filters.price[0].toLocaleString()} // Format the number as currency
// // //                 className="w-24 text-xs text-agLightGray border rounded-md text-sm p-2 text-center"
// // //                 onChange={(e) => {
// // //                   const numericValue = Number(e.target.value.replace(/,/g, "")); // Remove commas before updating the state
// // //                   setFilters({
// // //                     ...filters,
// // //                     price: [
// // //                       Math.min(numericValue || 0, filters.price[1]),
// // //                       filters.price[1],
// // //                     ],
// // //                   });
// // //                 }}
// // //               />
// // //             )}
// // //             <span className="text-sm font-bold text-gray-700 mx-2">-</span>
// // //             {/* Max Price Input */}
// // //             {isLoading ? (
// // //               <div className="w-24 h-10 bg-gray-200 animate-pulse rounded-md"></div>
// // //             ) : (
// // //               <input
// // //                 type="text"
// // //                 value={filters.price[1].toLocaleString()} // Format the number as currency
// // //                 className="w-24 text-xs text-agLightGray border rounded-md text-sm p-2 text-center"
// // //                 onChange={(e) => {
// // //                   const numericValue = Number(e.target.value.replace(/,/g, "")); // Remove commas before updating the state
// // //                   setFilters({
// // //                     ...filters,
// // //                     price: [
// // //                       filters.price[0],
// // //                       Math.max(numericValue || PRICE_MAX, filters.price[0]),
// // //                     ],
// // //                   });
// // //                 }}
// // //               />
// // //             )}
// // //           </div>
// // //         </div>

// // //         {/* Rating Filter */}
// // //         <div className="mb-6">
// // //           <h3 className="text-sm font-semibold font-nunito mb-4">Rating</h3>
// // //           {isLoading ? (
// // //             // Display a skeleton loader when loading
// // //             <div className="space-y-2">
// // //               {[...Array(4)].map((_, index) => (
// // //                 <div
// // //                   key={index}
// // //                   className="h-6 bg-gray-200 animate-pulse rounded-md"
// // //                 ></div>
// // //               ))}
// // //             </div>
// // //           ) : (
// // //             // Render the rating filter when not loading
// // //             [4, 3, 2, 1].map((rating) => (
// // //               <label
// // //                 key={rating}
// // //                 className="flex items-center space-x-4 cursor-pointer mb-2"
// // //               >
// // //                 {/* Custom Styled Radio Button */}
// // //                 <input
// // //                   type="radio"
// // //                   value={rating}
// // //                   name="rating"
// // //                   className="hidden peer" // Hide the default radio button
// // //                   onChange={() => setFilters({ ...filters, rating })}
// // //                 />
// // //                 <span className="w-4 h-4 rounded-full border-2 border-agLightGray peer-checked:bg-agPrimary"></span>

// // //                 {/* Stars */}
// // //                 <div className="flex items-center space-x-0">
// // //                   {[1, 2, 3, 4, 5].map((star) => (
// // //                     <img
// // //                       key={star}
// // //                       src={star <= rating ? fullYellowStar : fullGrayStar}
// // //                       alt={`${star} star`}
// // //                       className="w-5 h-5"
// // //                     />
// // //                   ))}
// // //                   <span className="text-xs text-gray-500 font-nunito ml-2">
// // //                     & above
// // //                   </span>
// // //                 </div>
// // //               </label>
// // //             ))
// // //           )}
// // //         </div>
// // //       </div>

// // //       {/* Right Main Content */}
// // //       <div className="w-3/4 p-4">
// // //         <div className="flex justify-between items-center mb-4">
// // //           <h3 className="font-nunito font-semibold text-agBlack text-md">
// // //             Search Results for "{query}"
// // //           </h3>

// // //           {/* Sorting Dropdown */}
// // //           <div className="w-56 relative">
// // //             <CustomDropdownNoSearch
// // //               options={[
// // //                 { value: "price", label: "Price (Lowest to Highest)" },
// // //                 { value: "-price", label: "Price (Highest to Lowest)" },
// // //                 { value: "rating", label: "Rating (Highest to Lowest)" },
// // //                 { value: "-rating", label: "Rating (Lowest to Highest)" },
// // //               ].map((option) => option.label)} // Pass only labels for display
// // //               value={
// // //                 {
// // //                   price: "Price (Lowest to Highest)",
// // //                   "-price": "Price (Highest to Lowest)",
// // //                   rating: "Rating (Highest to Lowest)",
// // //                   "-rating": "Rating (Lowest to Highest)",
// // //                 }[sort]
// // //               }
// // //               onChange={(label) =>
// // //                 setSort(
// // //                   {
// // //                     "Price (Lowest to Highest)": "price",
// // //                     "Price (Highest to Lowest)": "-price",
// // //                     "Rating (Highest to Lowest)": "rating",
// // //                     "Rating (Lowest to Highest)": "-rating",
// // //                   }[label],
// // //                 )
// // //               }
// // //               placeholder="Sort by"
// // //             />
// // //             {isLoading && (
// // //               <div className="absolute top-1/2 right-0 transform -translate-y-1/2">
// // //                 <span className="loader" />
// // //               </div>
// // //             )}
// // //           </div>
// // //         </div>

// // //         {/* Product Grid */}
// // //         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
// // //           {loadingDelay ? (
// // //             // Render skeleton loaders for the grid
// // //             Array.from({ length: 8 }).map((_, index) => (
// // //               <div
// // //                 key={index}
// // //                 className="h-64 bg-gray-200 animate-pulse rounded-md"
// // //               ></div>
// // //             ))
// // //           ) : isLoading ? (
// // //             <p className="text-center text-sm font-nunito text-gray-500">
// // //               Loading... 🚨
// // //             </p>
// // //           ) : data?.products.length > 0 ? (
// // //             renderProducts()
// // //           ) : (
// // //             <p className="text-center text-sm font-nunito text-gray-500">
// // //               No products found for your search.
// // //             </p>
// // //           )}
// // //         </div>

// // //         {/* Pagination */}
// // //         {data?.totalProducts > limit && (
// // //           <Pagination
// // //             currentPage={page}
// // //             totalPages={data?.totalPages || 1}
// // //             onPageChange={setPage}
// // //           />
// // //         )}
// // //       </div>
// // //     </div>
// // //   );
// // // };

// // // export default Search;
